<template>
  <main class="px-3">
    <Header hide-help-button :application-id="applicationId" />
    <div class="pb-5" />
    <CreditPullDisplay
      @accept="acceptBtn"
      :docHtml="docHtml"
      :acceptDisabled="acceptDisabled"
      :showAlert="showAlert"
      @signatureDrawn="signatureDrawn"
      @signatureCleared="signatureCleared"
      @alertClosed="alertClosed"
    />
  </main>

  <LoadingOverlay :loading="isLoading" />
  <ChatbotWrapper />
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { setLanguage } from "@/apps/core-app-onboarding/helpers/translations.helper";
import Header from "@/apps/core-app-onboarding/components/Header.vue";
import ChatbotWrapper from "@/apps/core-app-onboarding/components/ChatbotWrapper.vue";
import CreditPullDisplay from "@/shared-components/consumer/CreditPullDisplay.vue";
import { useI18n } from "@/apps/core-app-onboarding/composables/useI18n";
import { APIService } from "@/apps/core-app-onboarding/services/apiClient/api.service";
import { AndroidInterface } from "@/apps/core-app-onboarding/services/android.service";
import { localStoragePersonUuidKey } from "@/apps/core-app-onboarding/store/core-app-onboarding.module";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import {
  ERROR_CODES,
  ErrorHandler,
} from "@/apps/core-app-onboarding/helpers/errorHandlerWithLogger.helper";
import { RoutingService } from "@/shared-services/routing.service";
import { useRouter } from "vue-router";
import {
  stopAndSendCredolabFrontendData,
  sendCredolabDeviceReport,
} from "@/shared-services/credolab-api.service.ts";
import logAnalyticsEvent from "@/apps/core-app-onboarding/services/google-analytics-events.service";
import GoogleAnalyticsEventsHelper from "@/shared-helpers/google-analytics-events.helper";

declare const Android: AndroidInterface;

export default defineComponent({
  name: "CheckSignatureView",
  components: {
    Header,
    LoadingOverlay,
    ChatbotWrapper,
    CreditPullDisplay,
  },
  beforeMount() {
    setLanguage(this);
  },
  mounted() {
    this.fetchCreditPullDocument();
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const routingService = new RoutingService();
    const apiService = new APIService();
    const isLoading = ref<boolean>(false);
    const applicationId = Android.getCreditLineApplicationId();
    // ToDo: Get this value from Android interface
    const personUuid = localStorage.getItem(
      localStoragePersonUuidKey
    ) as string;
    const docHtml = ref<string>("");
    const acceptDisabled = ref<boolean>(true);
    const showAlert = ref<boolean>(false);
    const docRef = ref();

    const acceptBtn = async (base64Signature) => {
      isLoading.value = true;
      showAlert.value = false;
      acceptDisabled.value = true;
      try {
        const signature = base64Signature as string;

        await apiService.acceptSignature({
          signature,
          personUuid: personUuid,
          applicationId: applicationId,
          source: "in_store",
        });

        logAnalyticsEvent(
          GoogleAnalyticsEventsHelper.CoreAppOnboarding.OMCreditPullSigned,
          "Credit Line Open Market",
          undefined,
          undefined,
          () => {
            navigateToOpenMarketOffers();
          }
        );
      } catch (error) {
        console.error(error);
        ErrorHandler.logAPIError(error, ERROR_CODES.CREDIT_PULL_NETWORK_ERROR);
      }
      isLoading.value = false;
      acceptDisabled.value = false;
      showAlert.value = true;
    };

    const signatureDrawn = () => {
      acceptDisabled.value = false;
    };

    const signatureCleared = () => {
      acceptDisabled.value = true;
    };

    const fetchCreditPullDocument = async () => {
      isLoading.value = true;
      try {
        const res = await apiService.creditCheckPermission({
          applicationId: applicationId,
          personUuid: personUuid,
          source: "in_store",
        });

        if (res.data.isSigned) {
          let context = {
            applicationId: applicationId,
            personUuid: personUuid,
            caller: "CheckSignatureView fetchCreditPullDocument",
          };
          stopAndSendCredolabFrontendData(context);
          sendCredolabDeviceReport(context);
          navigateToOpenMarketOffers();
          return;
        } else {
          logAnalyticsEvent(
            GoogleAnalyticsEventsHelper.CoreAppOnboarding
              .OMCreditPullScreenView,
            "Credit Line Open Market"
          );
        }
        docHtml.value = res.data.document ?? "";
      } catch (error) {
        console.error(error);
        ErrorHandler.logAPIError(error, ERROR_CODES.PERMISSIONS_NETWORK_ERROR);
      }
      isLoading.value = false;
    };

    const navigateToOpenMarketOffers = () => {
      const deviceInfo = JSON.parse(Android.getDeviceInfo());
      router.push({
        name: "OpenMarketRegistrationView",
        query: {
          source: "in_store",
          person_uuid: personUuid,
          applicationId: applicationId,
          make_model: deviceInfo.makeModel,
          manufacturer: deviceInfo.manufacturer,
        },
      });
    };

    const modalScrollToBottomModal = () => {
      docRef.value.scrollTop = docRef.value.scrollHeight;
    };

    const alertClosed = () => {
      showAlert.value = false;
    };

    return {
      t,
      docRef,
      docHtml,
      isLoading,
      applicationId,
      routingService,
      acceptBtn,
      fetchCreditPullDocument,
      modalScrollToBottomModal,
      acceptDisabled,
      signatureDrawn,
      signatureCleared,
      showAlert,
      alertClosed,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
</style>
